import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, User, Mail, Phone, MapPin, Lock, Save, Crown, Calendar, CreditCard, Building2, FileText, Home, Award, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { TrustBadge, BadgeProgress } from '../components/TrustBadge';
import axios from 'axios';
import { API_URL } from '../utils/api';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, api, loading: authLoading, refreshUser, token } = useAuth();
  
  // Profile form state
  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    location: '',
    // Business seller fields
    seller_type: 'hobbyist', // 'hobbyist' or 'business'
    business_name: '',
    abn: '',
    street_address: '',
    address_suburb: '',
    address_state: '',
    address_postcode: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Email form state
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    password: ''
  });
  const [savingEmail, setSavingEmail] = useState(false);
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [badgeProfile, setBadgeProfile] = useState(null);
  const [subscribingBadge, setSubscribingBadge] = useState(false);

  // Check for badge subscription success
  useEffect(() => {
    if (searchParams.get('badge_success')) {
      toast.success('Badge display subscription activated!');
      refreshUser();
    } else if (searchParams.get('badge_cancelled')) {
      toast.info('Badge subscription cancelled');
    }
  }, [searchParams, refreshUser]);

  // Fetch badge info
  useEffect(() => {
    const fetchBadgeInfo = async () => {
      if (!user?.id) return;
      try {
        const res = await axios.get(`${API_URL}/api/users/${user.id}/profile`);
        setBadgeProfile(res.data);
      } catch (err) {
        console.error('Failed to fetch badge info', err);
      }
    };
    fetchBadgeInfo();
  }, [user?.id]);

  const handleBadgeSubscribe = async () => {
    setSubscribingBadge(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/badge-display/subscribe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error('Failed to start badge subscription', err);
      toast.error(err.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setSubscribingBadge(false);
    }
  };

  const handleBadgeCancel = async () => {
    try {
      await axios.post(
        `${API_URL}/api/badge-display/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Badge display subscription cancelled');
      refreshUser();
    } catch (err) {
      toast.error('Failed to cancel subscription');
    }
  };

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Populate form with current user data
    setProfile({
      name: user.name || '',
      phone: user.phone || '',
      location: user.location || '',
      // Business seller fields
      seller_type: user.seller_type || 'hobbyist',
      business_name: user.business_name || '',
      abn: user.abn || '',
      street_address: user.street_address || '',
      address_suburb: user.address_suburb || '',
      address_state: user.address_state || '',
      address_postcode: user.address_postcode || ''
    });
  }, [user, authLoading, navigate]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    
    if (!profile.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSavingProfile(true);
    try {
      await api().put('/profile', profile);
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (err) {
      console.error('Failed to update profile', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleEmailSave = async (e) => {
    e.preventDefault();
    
    if (!emailForm.newEmail || !emailForm.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setSavingEmail(true);
    try {
      await api().put('/profile/email', {
        new_email: emailForm.newEmail,
        password: emailForm.password
      });
      await refreshUser();
      setEmailForm({ newEmail: '', password: '' });
      toast.success('Email updated successfully');
    } catch (err) {
      console.error('Failed to update email', err);
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);
    try {
      await api().put('/profile/password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully');
    } catch (err) {
      console.error('Failed to change password', err);
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-teal-600 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Account Settings
        </h1>
        <p className="text-stone-500 mb-8">
          Manage your profile and account preferences
        </p>

        {/* Subscription Status Card */}
        <div className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Crown className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="font-semibold text-stone-900">Subscription Status</h2>
              <p className="text-sm text-stone-500">Your current membership</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-stone-500">Status</p>
              <p className="font-medium text-stone-900 capitalize">
                {user.subscription_status === 'active' && (
                  <span className="text-green-600">Active</span>
                )}
                {user.subscription_status === 'trial' && (
                  <span className="text-blue-600">Free Trial</span>
                )}
                {user.subscription_status === 'cancelled' && (
                  <span className="text-teal-600">Cancelled</span>
                )}
                {user.subscription_status === 'past_due' && (
                  <span className="text-orange-600">Payment Due</span>
                )}
                {(!user.subscription_status || user.subscription_status === 'free') && (
                  <span className="text-stone-500">No Subscription</span>
                )}
              </p>
            </div>
            {user.subscription_plan && (
              <div>
                <p className="text-stone-500">Plan</p>
                <p className="font-medium text-stone-900 capitalize">{user.subscription_plan}</p>
              </div>
            )}
            {user.trial_ends_at && user.subscription_status === 'trial' && (
              <div>
                <p className="text-stone-500">Trial Ends</p>
                <p className="font-medium text-stone-900">{formatDate(user.trial_ends_at)}</p>
              </div>
            )}
            <div>
              <p className="text-stone-500">Member Since</p>
              <p className="font-medium text-stone-900">{formatDate(user.created_at)}</p>
            </div>
          </div>
          
          {user.is_seller && (
            <div className="mt-4 pt-4 border-t border-stone-100">
              <Button
                onClick={() => navigate('/seller/settings')}
                variant="outline"
                className="w-full"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Manage Subscription
              </Button>
            </div>
          )}
        </div>

        {/* Profile Information */}
        <form onSubmit={handleProfileSave} className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <User className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="font-semibold text-stone-900">Profile Information</h2>
              <p className="text-sm text-stone-500">Update your personal details</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Your name"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="04XX XXX XXX"
                className="mt-1"
              />
              <p className="text-xs text-stone-500 mt-1">Australian mobile number</p>
            </div>
            
            <div>
              <Label htmlFor="location">Location (Public)</Label>
              <Input
                id="location"
                type="text"
                value={profile.location}
                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                placeholder="City, State"
                className="mt-1"
              />
              <p className="text-xs text-stone-500 mt-1">This is displayed publicly on your listings</p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={savingProfile}
            className="w-full mt-6 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
          >
            {savingProfile ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Profile
              </>
            )}
          </Button>
        </form>

        {/* Business/Seller Information - Only show for sellers */}
        {user.is_seller && (
          <form onSubmit={handleProfileSave} className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <h2 className="font-semibold text-stone-900">Seller Information</h2>
                <p className="text-sm text-stone-500">Required for legal compliance</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Legal Requirement:</strong> Under Australian Consumer Law, you must disclose whether you are a business seller or hobbyist. Business sellers must provide their ABN and business details.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="seller_type">Seller Type *</Label>
                <Select
                  value={profile.seller_type}
                  onValueChange={(value) => setProfile({ ...profile, seller_type: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select seller type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hobbyist">Hobbyist / Occasional Seller</SelectItem>
                    <SelectItem value="business">Business Seller (ABN required)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-stone-500 mt-1">
                  Select "Business" if you make regular sales with profit motive
                </p>
              </div>

              {profile.seller_type === 'business' && (
                <>
                  <div>
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      type="text"
                      value={profile.business_name}
                      onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                      placeholder="Your registered business name"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="abn">Australian Business Number (ABN) *</Label>
                    <Input
                      id="abn"
                      type="text"
                      value={profile.abn}
                      onChange={(e) => setProfile({ ...profile, abn: e.target.value.replace(/\s/g, '') })}
                      placeholder="XX XXX XXX XXX"
                      maxLength={14}
                      className="mt-1"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      Your 11-digit ABN (displayed publicly on your profile)
                    </p>
                  </div>
                </>
              )}

              <div className="pt-4 border-t border-stone-200">
                <Label className="flex items-center gap-2 mb-3">
                  <Home className="h-4 w-4 text-stone-500" />
                  Contact Address (Private - Not publicly displayed)
                </Label>
                <p className="text-xs text-stone-500 mb-4">
                  Required for legal compliance. May be disclosed to buyers in disputes or to authorities if required.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="street_address">Street Address *</Label>
                    <Input
                      id="street_address"
                      type="text"
                      value={profile.street_address}
                      onChange={(e) => setProfile({ ...profile, street_address: e.target.value })}
                      placeholder="123 Example Street"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="address_suburb">Suburb *</Label>
                      <Input
                        id="address_suburb"
                        type="text"
                        value={profile.address_suburb}
                        onChange={(e) => setProfile({ ...profile, address_suburb: e.target.value })}
                        placeholder="Suburb"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address_postcode">Postcode *</Label>
                      <Input
                        id="address_postcode"
                        type="text"
                        value={profile.address_postcode}
                        onChange={(e) => setProfile({ ...profile, address_postcode: e.target.value })}
                        placeholder="2000"
                        maxLength={4}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="address_state">State *</Label>
                    <Select
                      value={profile.address_state}
                      onValueChange={(value) => setProfile({ ...profile, address_state: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NSW">New South Wales</SelectItem>
                        <SelectItem value="VIC">Victoria</SelectItem>
                        <SelectItem value="QLD">Queensland</SelectItem>
                        <SelectItem value="WA">Western Australia</SelectItem>
                        <SelectItem value="SA">South Australia</SelectItem>
                        <SelectItem value="TAS">Tasmania</SelectItem>
                        <SelectItem value="ACT">Australian Capital Territory</SelectItem>
                        <SelectItem value="NT">Northern Territory</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={savingProfile}
              className="w-full mt-6 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
            >
              {savingProfile ? (
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Seller Information
                </>
              )}
            </Button>
          </form>
        )}

        {/* Email Address */}
        <form onSubmit={handleEmailSave} className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Mail className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="font-semibold text-stone-900">Email Address</h2>
              <p className="text-sm text-stone-500">Current: {user.email}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="newEmail">New Email Address</Label>
              <Input
                id="newEmail"
                type="email"
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                placeholder="new@example.com"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="emailPassword">Current Password</Label>
              <Input
                id="emailPassword"
                type="password"
                value={emailForm.password}
                onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                placeholder="Enter your password to confirm"
                className="mt-1"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={savingEmail}
            className="w-full mt-6 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
          >
            {savingEmail ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Update Email
              </>
            )}
          </Button>
        </form>

        {/* Change Password */}
        <form onSubmit={handlePasswordSave} className="bg-white border border-stone-200 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="font-semibold text-stone-900">Change Password</h2>
              <p className="text-sm text-stone-500">Update your account password</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                placeholder="Enter current password"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="Enter new password"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                className="mt-1"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={savingPassword}
            className="w-full mt-6 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
          >
            {savingPassword ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
